import Vue from 'vue'
import Vuex from 'vuex'
import snEvaultService from '../services/snEvaultService'
import gatorToastService from '~/services/gatorToastService'
import Cookies from 'js-cookie'

Vue.use(Vuex)
Vue.config.devtools = true

export const store = new Vuex.Store({
  state: () => ({
    _appInitialized: false,
    _orgs: [],
    _orgGuid: Cookies.get('evault_org_guid'),
    _mersOrgId: Cookies.get('evault_mers_org_id'),
    _headerText: '',
    _subtitleText: '',
    _headerTabs: [],
    _activeHeaderTab: '',
    _breadcrumbs: [],
    _user: { loading: true },
    _showHeaderLoaderSkeleton: false,
    _isStaging: Cookies.get('evault_is_staging') === 'true',
    _timeoutModalTimeout: null,
    _timeoutModalVisible: false,
    _viewAsCompany: null,
    _profileSettingsVisible: false,
    _profileSettingsInitialTab: 0
  }),
  getters: {
    appInitialized (state) {
      return state._appInitialized
    },
    orgs (state) {
      return state._orgs
    },
    orgGuid (state) {
      return state._orgGuid
    },
    mersOrgId (state) {
      return state._mersOrgId
    },
    headerText (state) {
      return state._headerText
    },
    subtitleText (state) {
      return state._subtitleText
    },
    breadcrumbs (state) {
      return state._breadcrumbs
    },
    headerTabs (state) {
      return state._headerTabs
    },
    activeHeaderTab (state) {
      return state._activeHeaderTab
    },
    timeoutModalVisible (state) {
      return state._timeoutModalVisible
    },
    user (state) {
      return state._user
    },
    showHeaderLoaderSkeleton (state) {
      return state._showHeaderLoaderSkeleton
    },
    isStaging (state) {
      return state._isStaging
    },
    viewAsCompany (state) {
      return state._viewAsCompany
    },
    profileSettingsVisible (state) {
      return state._profileSettingsVisible
    },
    profileSettingsInitialTab (state) {
      return state._profileSettingsInitialTab
    }
  },
  mutations: {
    setAppInitialized (state, newValue) {
      state._appInitialized = newValue
    },
    setOrgs (state, newValue) {
      state._orgs = newValue
    },
    setMersOrgId (state, newMersOrgId) {
      if (newMersOrgId == null) {
        Cookies.remove('evault_mers_org_id')
      } else {
        Cookies.set('evault_mers_org_id', newMersOrgId)
      }
      state._mersOrgId = newMersOrgId
    },
    setOrgGuid (state, newOrgGuid) {
      if (newOrgGuid == null) {
        Cookies.remove('evault_org_guid')
      } else {
        Cookies.set('evault_org_guid', newOrgGuid)
      }
      state._orgGuid = newOrgGuid
    },
    setHeaderText (state, newHeaderText) {
      if (typeof newHeaderText === 'string') {
        state._headerText = newHeaderText.replaceAll('_', ' ')
      } else {
        state._headerText = newHeaderText
      }
    },
    setSubtitleText (state, newSubtitleText) {
      if (typeof newSubtitleText === 'string') {
        state._subtitleText = newSubtitleText.replaceAll('_', ' ')
      } else {
        state._subtitleText = newSubtitleText
      }
    },
    setBreadcrumbs (state, newBreadcrumbs) {
      if (newBreadcrumbs.length) {
        newBreadcrumbs[newBreadcrumbs.length - 1].disabled = true
      }
      state._breadcrumbs = newBreadcrumbs
    },
    setHeaderTabs (state, newHeaderTabs) {
      state._headerTabs = newHeaderTabs
    },
    setActiveHeaderTab (state, newActiveHeaderTab) {
      state._activeHeaderTab = newActiveHeaderTab
    },
    setIsStaging (state, value) {
      Cookies.set('evault_is_staging', value)
      state._isStaging = value
    },
    becomeCompany (state, company) {
      if (company == null) {
        sessionStorage.removeItem('evault_view_as_company_guid')
      } else {
        sessionStorage.setItem('evault_view_as_company_guid', company.guid)
      }
      state._viewAsCompany = company
    },
    // These nine stay the app's toast API - ~525 call sites commit them - so only
    // the bodies move. New code should call $toasterService directly.
    setToast (state, newToast) {
      return gatorToastService.setToast(newToast)
    },
    setSuccessToast (state, { message = 'Success', description = '' }) {
      return gatorToastService.setSuccessToast({ message, description })
    },
    setInfoToast (state, { message = 'Information', description = '' }) {
      return gatorToastService.setInfoToast({ message, description })
    },
    setErrorToast (state, { message = 'Error', description = '' }) {
      return gatorToastService.setErrorToast({ message, description })
    },
    setWarningToast (state, { message = 'Warning', description = '' }) {
      return gatorToastService.setWarningToast({ message, description })
    },
    setSuccessActionToast (state, { message = 'Success', description = '', actionText = '', actionData = {} }) {
      return gatorToastService.setSuccessActionToast({ message, description, actionText, actionData })
    },
    setSuccessStickyActionToast (state, { message = 'Success', description = '', actionText = '', actionData = {} }) {
      return gatorToastService.setSuccessStickyActionToast({ message, description, actionText, actionData })
    },
    setErrorStickyActionToast (state, { message = 'Error', description = '', actionText = '', actionData = {} }) {
      return gatorToastService.setErrorStickyActionToast({ message, description, actionText, actionData })
    },
    setErrorToastType (state, { message = 'Error', description = '' }) {
      return gatorToastService.setErrorToastType({ message, description })
    },
    setUser (state, user) {
      const productionEnabled = snEvaultService.hasPermission(user, 'productionEnabled')
      const stagingEnabled = snEvaultService.hasPermission(user, 'stagingEnabled')

      if (productionEnabled && !stagingEnabled) {
        state._isStaging = false
      } else if (stagingEnabled && !productionEnabled) {
        state._isStaging = true
      }
      user.loading = false
      state._user = user
    },
    setShowHeaderLoaderSkeleton (state, value) {
      state._showHeaderLoaderSkeleton = value
    },
    resetAppTimeout (state, minutesRemaining) {
      const warningModalTime = minutesRemaining ? Math.floor(minutesRemaining * 60000) : 10 * 60000 // 10 minutes (evaultSessionTimeoutModal lasts 5 minutes for 15 total idle time)
      state._timeoutModalVisible = false

      clearTimeout(state._timeoutModalTimeout)
      state._timeoutModalTimeout = setTimeout(() => {
        state._timeoutModalVisible = true
      }, warningModalTime)
    },
    setTimeoutModalVisible (state, visible) {
      state._timeoutModalVisible = visible
    },
    setProfileSettingsVisible (state, visible) {
      state._profileSettingsVisible = visible
    },
    setProfileSettingsInitialTab (state, newInitialTab) {
      state._profileSettingsInitialTab = newInitialTab
    }
  }
})
